import type { Chapter } from '../types';
import type { InspectorFailureCategory } from './recoveryAction.service';

export const INSPECTOR_INPUT_CHECKPOINT_VERSION = 'inspector-input-v1';
export const RECOVERY_CHECKPOINT_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

export type RecoverySafeResumePoint =
  | 'INSPECTOR_VALIDATION'
  | 'PublicationEditor'
  | 'Archivist'
  | 'SaveChapter'
  | 'PHYSICAL_CONTINUITY_EXTRACTION';

export interface InspectorInputCheckpoint {
  checkpointVersion: string;
  projectId: string;
  chapterNumber: number;
  runId: string;
  safeResumePoint: 'INSPECTOR_VALIDATION';
  failureCategory?: InspectorFailureCategory;
  draft: Chapter;
  fourBeatProvenance: unknown;
  authoritativeSnapshotHash: string;
  roadmapHash: string;
  createdAt: string;
  canonical: false;
  expiresAt: string;
}

export function buildInspectorInputCheckpointPath(
  uid: string,
  projectId: string,
  chapterNumber: number,
): string {
  return `users/${uid}/projects/${projectId}/checkpoints/chapter-${chapterNumber}-inspector-input.json`;
}

export function hashRecoveryContext(value: unknown): string {
  const text = JSON.stringify(value ?? null);
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  }
  return `h${hash.toString(16)}`;
}

export function buildInspectorInputCheckpoint(params: {
  projectId: string;
  chapterNumber: number;
  runId: string;
  draft: Chapter;
  authoritativeSnapshotHash: string;
  roadmapHash: string;
  failureCategory?: InspectorFailureCategory;
  now?: number;
}): InspectorInputCheckpoint {
  const now = params.now ?? Date.now();
  return {
    checkpointVersion: INSPECTOR_INPUT_CHECKPOINT_VERSION,
    projectId: params.projectId,
    chapterNumber: params.chapterNumber,
    runId: params.runId,
    safeResumePoint: 'INSPECTOR_VALIDATION',
    draft: params.draft,
    fourBeatProvenance: params.draft.fourBeatProvenance ?? null,
    authoritativeSnapshotHash: params.authoritativeSnapshotHash,
    roadmapHash: params.roadmapHash,
    failureCategory: params.failureCategory,
    createdAt: new Date(now).toISOString(),
    canonical: false,
    expiresAt: new Date(now + RECOVERY_CHECKPOINT_RETENTION_MS).toISOString(),
  };
}

export function isRecoveryCheckpointExpired(checkpoint: Pick<InspectorInputCheckpoint, 'expiresAt'>, now = Date.now()): boolean {
  return Date.parse(checkpoint.expiresAt) <= now;
}

export function validateInspectorCheckpointResume(params: {
  checkpoint: InspectorInputCheckpoint;
  projectId: string;
  authoritativeSnapshotHash: string;
  roadmapHash: string;
}): { ok: true } | { ok: false; reason: string } {
  if (params.checkpoint.projectId !== params.projectId) {
    return { ok: false, reason: 'checkpoint_project_mismatch' };
  }
  if (isRecoveryCheckpointExpired(params.checkpoint)) {
    return { ok: false, reason: 'checkpoint_retention_expired' };
  }
  if (params.checkpoint.authoritativeSnapshotHash !== params.authoritativeSnapshotHash) {
    return { ok: false, reason: 'authoritative_snapshot_hash_mismatch' };
  }
  if (params.checkpoint.roadmapHash !== params.roadmapHash) {
    return { ok: false, reason: 'roadmap_hash_mismatch' };
  }
  return { ok: true };
}

export function shouldDeleteRecoveryCheckpoint(params: {
  explicitDiscard: boolean;
  chapterCommitted: boolean;
  retentionExpired: boolean;
  projectDeleted: boolean;
  integrityFailed: boolean;
}): boolean {
  return (
    params.explicitDiscard ||
    params.chapterCommitted ||
    params.retentionExpired ||
    params.projectDeleted ||
    params.integrityFailed
  );
}
